/**
 * SizilPro — __root.tsx
 * TanStack Router root route. Wraps private pages in AppShell.
 * Auth gating is rendered declaratively to avoid navigate/useEffect loops.
 */

import {
  createRootRouteWithContext,
  Navigate,
  Outlet,
  useRouterState,
} from "@tanstack/react-router";
import type { QueryClient } from "@tanstack/react-query";
import { AppShell } from "@/shared/components/AppShell";
import { useAuthStore } from "@/store/authStore";

interface RouterContext {
  queryClient: QueryClient;
}

const PUBLIC_PATHS = ["/login", "/onboarding", "/sign-up", "/proposal-render"];

function isPublicPath(pathname: string) {
  return PUBLIC_PATHS.some((p) => pathname.startsWith(p));
}

export const Route = createRootRouteWithContext<RouterContext>()({
  component: RootLayout,
});

function RootLayout() {
  const pathname = useRouterState({
    select: (state) => state.location.pathname,
  });

  const user = useAuthStore((s) => s.user);
  const isLoading = useAuthStore((s) => s.isLoading);
  const initialized = useAuthStore((s) => s.initialized);

  const publicPath = isPublicPath(pathname);

  // Only show the full-screen spinner on the very first load (before the session
  // is resolved). Once initialized, subsequent auth refreshes must not unmount
  // the running app — that would wipe all in-progress form state.
  if (isLoading && !initialized && !publicPath) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#0f0f0f] text-white">
        <div className="h-5 w-5 animate-spin rounded-full border-2 border-white/20 border-t-white/80" />
      </div>
    );
  }

  if (!isLoading && !user && !publicPath) {
    return (
      <Navigate
        to="/login"
        search={{ redirect: pathname }}
        replace
      />
    );
  }

  if (!isLoading && user && pathname === "/login") {
    return <Navigate to="/" replace />;
  }

  if (publicPath) {
    return <Outlet />;
  }

  return (
    <AppShell>
      <Outlet />
    </AppShell>
  );
}